import { QueryClient } from '@tanstack/react-query';

const DEFAULT_OPTIONS = {
  queries: {
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: true,
    // Не обновлять при переподключении (Telegram WebView специфика)
    refetchOnReconnect: false,
  },
  mutations: {
    retry: 1,
  },
} as const;

/** Factory — call once per app boundary (TMA, landing SSR, etc.) so each
 * gets its own cache. Use this in Next.js Providers to avoid a shared
 * singleton leaking between requests. */
export const createQueryClient = () => new QueryClient({ defaultOptions: DEFAULT_OPTIONS });

/** Shared singleton for the standalone TMA (Vite build) where module-level
 * creation is acceptable. */
export const queryClient = createQueryClient();

// Query keys factory для типобезопасности и консистентности
export const queryKeys = {
  // Profile
  profile: ['profile'] as const,
  profileStats: (userId?: string) => ['profile', 'stats', userId] as const,
  xpStatus: ['xp', 'status'] as const,
  xpConfig: ['xp', 'config'] as const,
  levelBonuses: ['xp', 'bonuses'] as const,

  // Chicken tap reward
  chickenTick: ['chicken', 'tick'] as const,

  // Home
  homeSnapshot: ['home', 'snapshot'] as const,
  comebackBonus: ['home', 'comebackBonus'] as const,
  channels: ['home', 'channels'] as const,
  boostChannels: ['home', 'boostChannels'] as const,
  referralProgress: ['home', 'referralProgress'] as const,
  profileTasks: ['home', 'profileTasks'] as const,
  dailyBonus: ['home', 'dailyBonus'] as const,
  topDrops: ['home', 'topDrops'] as const,

  // Cases
  cases: ['cases'] as const,
  caseDetails: (caseId: string) => ['cases', caseId] as const,
  caseItem: (caseId: string, hashName: string) => ['cases', caseId, 'item', hashName] as const,

  // Inventory
  inventory: (filter: string) => ['inventory', filter] as const,
  inventoryItem: (itemId: string) => ['inventory', 'item', itemId] as const,

  // Shop
  shopItems: (filters: Record<string, unknown>) => ['shop', 'items', filters] as const,
  shopSkins: (weaponId: string, filters?: Record<string, unknown>) => ['shop', 'skins', weaponId, filters] as const,
  exchangeRate: ['shop', 'exchangeRate'] as const,
  paymentSettings: ['shop', 'paymentSettings'] as const,

  // Upgrade
  upgradeTargets: (sourceItemId: string, filters: Record<string, unknown>) =>
    ['upgrade', 'targets', sourceItemId, filters] as const,
  upgradeInventory: (minPrice: number) =>
    ['upgrade', 'inventory', minPrice] as const,

  // Auctions
  auctions: (status?: string) => ['auctions', status] as const,
  auctionDetails: (auctionId: string) => ['auctions', auctionId] as const,
  auctionBids: (auctionId: string) => ['auctions', auctionId, 'bids'] as const,

  // Draws
  draws: ['draws', 'active'] as const,
  drawParticipants: (drawId: string) => ['draws', drawId, 'participants'] as const,
  drawsHistory: (drawType?: string) => ['draws', 'history', drawType ?? 'all'] as const,

  // Giveaways
  giveawayDetails: (giveawayId: string) => ['giveaway', giveawayId] as const,
  giveawayParticipants: (giveawayId: string) => ['giveaway', giveawayId, 'participants'] as const,

  // Daily Giveaways
  dailyGiveaways: ['dailyGiveaways'] as const,
  dailyGiveawayDetails: (giveawayId: string) => ['dailyGiveaways', giveawayId] as const,
  dailyGiveawayLeaderboard: (giveawayId: string) => ['dailyGiveaways', giveawayId, 'leaderboard'] as const,
  dailyGiveawayWinners: (giveawayId: string) => ['dailyGiveaways', giveawayId, 'winners'] as const,

  // Wheel
  wheelStatus: ['wheel', 'status'] as const,

  // Contracts
  contractsInventory: ['contracts', 'inventory'] as const,

  // Referrals
  referrals: (page?: number) => ['referrals', page] as const,
  referralStats: ['referrals', 'stats'] as const,

  // Promocode
  activeBuff: ['promocode', 'activeBuff'] as const,
  globalBuff: ['promocode', 'globalBuff'] as const,

  // Case Battles
  caseBattles: ['caseBattles'] as const,
  caseBattlesCount: ['caseBattles', 'count'] as const,
  caseBattleDetails: (battleId: string) => ['caseBattles', battleId] as const,
  caseBattleHistoryAll: ['caseBattles', 'history', 'all'] as const,
  caseBattleHistoryMine: ['caseBattles', 'history', 'mine'] as const,
  userActiveBattlesCount: ['caseBattles', 'userActiveCount'] as const,

  // Rush Mid
  rushMidHistory: ['rushMid', 'history'] as const,
  rushMidRating: (mode: number) => ['rushMid', 'rating', mode] as const,
  rushMidSkins: ['rushMid', 'skins'] as const,
  rushMidOpenGames: ['rushMid', 'openGames'] as const,
  rushMidOpenGame: (matchId: string) => ['rushMid', 'openGame', matchId] as const,
  rushMidLiveGames: ['rushMid', 'liveGames'] as const,

  // Roulette
  rouletteGames: ['roulette', 'games'] as const,
  rouletteHistory: ['roulette', 'history'] as const,
  rouletteFeed: ['roulette', 'feed'] as const,
  rouletteState: ['roulette', 'state'] as const,
  rouletteMatch: (matchId: string) => ['roulette', 'match', matchId] as const,
  rouletteFairness: (matchId: string) => ['roulette', 'fairness', matchId] as const,

  // Rush Raffle
  rushRaffleRounds: ['rushRaffle', 'rounds'] as const,
  rushRaffleRoundDetail: (roundId: string) => ['rushRaffle', 'round', roundId] as const,
  rushRaffleMatchAward: (matchId: string) => ['rushRaffle', 'matchAward', matchId] as const,
  rushRaffleWinners: ['rushRaffle', 'winners'] as const,

  // Realtime
  onlineCount: ['realtime', 'online'] as const,

  // Achievements (PR6 — backend at apps/backend/src/achievement/)
  achievementsCatalog: ['achievements', 'catalog'] as const,
  achievementsMine: ['achievements', 'mine'] as const,
  achievementsUser: (userId?: string) => ['achievements', 'user', userId] as const,
  achievementsStats: (key?: string) => ['achievements', 'stats', key] as const,
  achievementsRecentReceivers: (key?: string) => ['achievements', 'recent-receivers', key] as const,
} as const;
